import { TextField, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { Fragment, useRef, useState } from 'react'
import { ProductValidationList } from './ProductValidationList'
import { ProductValidation } from './ProductValidation'
import { useApi } from '../../../hooks/useApi'
import { State } from '../../_EOS/State'
import {
  getPickingRouteProducts,
  validateSku,
  resetAllValidated,
  resetSkuValidation
} from '../api'
import { SkuValidationDto } from '../api/types'
import { AxiosResponse } from 'axios'
import { State as StateType, Status } from '../../../hooks/useFetchState'
import { Modal } from '../../_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { PreviewSkuImage } from '../Modals/PreviewSkuImage'
import { ConfirmAction } from '../../ConfirmAction'

interface ProductValidationModalProps {
  pickingRouteId: number
  validateSkuCallApi: (apiCall: Promise<AxiosResponse<any>>) => Promise<any>
  validateSkuState: StateType<unknown>
}

export const ProductValidationModal: React.FC<ProductValidationModalProps> = ({
  pickingRouteId,
  validateSkuCallApi,
  validateSkuState
}) => {
  const [skuError, setSkuError] = useState<{
    message: string
    success?: boolean
  }>({
    message: '',
    success: true
  })
  const { openModal, isOpen, closeModal, extendedProps } =
    useModal<number>('Imágen de producto')
  const classes = useStyles()
  const [productValidating, setProductValidating] = useState('')
  const [productListValidation, setProductListValidation] = useState<
    ProductValidation[] | undefined
  >([])
  const [Loading, setIsLoading] = useState<boolean>(false)
  const [confirmConfig, setConfirmConfig] = useState<{
    title: string
    confirmText: string
    negateText: string
    onConfirm: () => void
    open: boolean
  }>({
    title: '',
    confirmText: '',
    negateText: '',
    onConfirm: () => {},
    open: false
  })
  const { callApi, state } = useApi<ProductValidation[]>()
  const skuInputRef = useRef<HTMLInputElement>(null)
  const openConfirm = (
    title: string,
    confirmText: string,
    negateText: string,
    onConfirm: () => void
  ) => {
    setConfirmConfig({
      title,
      confirmText,
      negateText,
      onConfirm: () => {
        onConfirm()
        handleCloseConfirm()
      },
      open: true
    })
  }

  // Función para actualizar solo los SKUs afectados por la API
  const updateProductValidationAfterApiCall = (
    validatedSkus: SkuValidationDto[]
  ) => {
    setProductListValidation((prev) => {
      if (!prev) return prev

      const updatedList = [...prev]
      validatedSkus.forEach((validatedSku) => {
        const index = updatedList.findIndex(
          (item) => item.skuId === validatedSku.skuId
        )
        if (index !== -1) {
          updatedList[index] = {
            ...updatedList[index],
            validated: true,
            validationQty: validatedSku.validationQty
          }
        }
      })
      return updatedList
    })
  }

  const updateProductValidationAfterReset = (resetSkus: SkuValidationDto[]) => {
    setProductListValidation((prev) => {
      if (!prev) return prev

      const updatedList = [...prev]
      resetSkus.forEach((resetSku) => {
        const index = updatedList.findIndex(
          (item) => item.skuId === resetSku.skuId
        )
        if (index !== -1) {
          updatedList[index] = {
            ...updatedList[index],
            validated: false,
            validationQty: 0,
            touched: Date.now()
          }
        }
      })
      return updatedList
    })
  }

  const handleCloseConfirm = () => {
    setIsLoading(true)
    setConfirmConfig((prev) => ({ ...prev, open: false }))
    setIsLoading(false)
  }

  const requestPickingRouteProducts = () => {
    callApi(getPickingRouteProducts(pickingRouteId))
      .then((resp: ProductValidation[]) => {
        setProductListValidation(resp)
      })
      .catch((error) => console.log(error))
  }

  const requestValidatePickingProduct = (body: SkuValidationDto[]) => {
    validateSkuCallApi(validateSku(pickingRouteId, body))
      .then(() => {
        setSkuError({
          message: 'Guardado con éxito',
          success: true
        })
        // Solo actualizar el SKU específico, no toda la lista
        updateProductValidationAfterApiCall(body)
      })
      .catch((error) => setSkuError({ message: error, success: false }))
      .finally(() => {
        focusSkuInput()
      })
  }

  const requestSkuValidated = (body: SkuValidationDto[]) => {
    callApi(resetSkuValidation(pickingRouteId, body))
      .then(() => {
        setSkuError({
          message: 'Liberado con éxito',
          success: true
        })
        // actualizar solo el SKU indicado
        updateProductValidationAfterReset(body)
      })
      .catch((error) => setSkuError({ message: error, success: false }))
      .finally(() => {
        focusSkuInput()
      })
  }

  const requestResetPickingRouteValidated = () => {
    callApi(resetAllValidated(pickingRouteId))
      .then(() => {
        setSkuError({
          message: 'Liberado con éxito',
          success: true
        })
        // Resetear todos los SKUs localmente
        setProductListValidation((prev) => {
          if (!prev) return prev
          return prev.map((product) => ({
            ...product,
            validated: false,
            validationQty: 0,
            touched: 0
          }))
        })
      })
      .catch((error) => setSkuError({ message: error, success: false }))
      .finally(() => {
        focusSkuInput()
      })
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setProductValidating(event.target.value)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && productListValidation) {
      const foundIndex = productListValidation.findIndex(
        (item) =>
          item.sku === productValidating || item.ean13 === productValidating
      )

      // Si no lo encontramos, seteamos error y retornamos
      if (foundIndex === -1) {
        setSkuError({
          message: 'El SKU/EAN que intenta validar no existe',
          success: false
        })
        return
      }

      if (foundIndex >= 0) {
        setSkuError({
          message: '',
          success: undefined
        })
        // Si lo encontramos, pero ya fue validado, mostrar mensaje
        if (productListValidation[foundIndex].validated) {
          setSkuError({
            message: 'El producto que esta ingresado ya se encuentra validado',
            success: true
          })
          return
        }
        // SKU is in the list, update quantity and epoch without exceeding max
        let newQuantity =
          (productListValidation[foundIndex].validationQty || 0) + 1
        if (newQuantity > productListValidation[foundIndex].realQty) {
          newQuantity = productListValidation[foundIndex].realQty
        }

        const newSkus = [...productListValidation]
        newSkus[foundIndex] = {
          ...newSkus[foundIndex],
          validationQty: newQuantity,
          touched: Date.now()
        }

        // Sort SKUs by touched descending
        newSkus.sort((a, b) => b.touched - a.touched)
        setProductListValidation(newSkus)

        if (newQuantity === productListValidation[foundIndex].realQty) {
          requestValidatePickingProduct([
            {
              skuId: productListValidation[foundIndex].skuId,
              validationQty: newQuantity || 0
            }
          ])
        }
      }
      setProductValidating('') // Clear the input field

      focusSkuInput()
    }
  }

  React.useEffect(() => {
    requestPickingRouteProducts()
  }, [])

  const focusSkuInput = () => {
    setTimeout(() => {
      skuInputRef.current?.focus()
    }, 0)
  }

  const closeUnitaryProductValidation = (skuId: number) => {
    const product = productListValidation?.find((el) => el.skuId === skuId)
    if (!product) return

    requestValidatePickingProduct([
      {
        skuId: product.skuId,
        validationQty: product.validationQty || 0
      }
    ])
  }

  const resetUnitaryProductValidation = (skuId: number) => {
    requestSkuValidated([
      {
        skuId,
        validationQty: 0
      }
    ])
  }
  const handleShowProductImage = (product: ProductValidation) =>
    openModal('md', product.skuId, `Imágen de producto ${product.productName}`)

  const handleResetAllClick = () => {
    openConfirm(
      '¿Esta seguro que desea reiniciar la validación de todos los productos?',
      'Aceptar',
      'Cancelar',
      requestResetPickingRouteValidated
    )
  }
  return (
    <Fragment>
      <State state={state} loadingMessage='Obteniendo productos a validar'>
        <TextField
          fullWidth
          autoFocus
          label='Ingresar SKU/EAN'
          value={productValidating}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          variant='outlined'
          disabled={validateSkuState.status === Status.Loading}
          error={skuError.success === false}
          helperText={skuError.message}
          inputRef={skuInputRef}
          FormHelperTextProps={{
            className: skuError.success
              ? classes.helperText
              : classes.helperTextBase
          }}
        />
        {(productListValidation || []).map((product, index) => (
          <ProductValidationList
            isLoading={validateSkuState.status === Status.Loading}
            key={index}
            text={`${product.sku} - ${product.ean13}`}
            subtitle={`${product.validationQty || 0} de ${product.realQty}`}
            success={product.validationQty === product.realQty}
            closeProductValidation={() =>
              closeUnitaryProductValidation(product.skuId)
            }
            resetProductValidation={() =>
              resetUnitaryProductValidation(product.skuId)
            }
            onClick={() => handleShowProductImage(product)}
            isValidated={product.validated}
          />
        ))}

        <div className={classes.resetButton}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            onClick={handleResetAllClick}
            disabled={!productListValidation?.some((p) => p.validated)} // Deshabilita si hay algún no validado
          >
            Reiniciar validación completa
          </Button>
        </div>
      </State>
      <Modal open={isOpen} onClose={closeModal}>
        {!extendedProps && <h1>No ha seleccionado producto</h1>}
        {extendedProps && <PreviewSkuImage skuId={extendedProps} />}
      </Modal>
      <Modal onClose={handleCloseConfirm} open={confirmConfig.open}>
        <ConfirmAction
          handleCloseModal={handleCloseConfirm}
          handleSubmitAction={confirmConfig.onConfirm}
          confirmTitle={confirmConfig.title}
          confirmText={confirmConfig.confirmText}
          negateText={confirmConfig.negateText}
          isLoading={Loading}
        />
      </Modal>
    </Fragment>
  )
}

const useStyles = makeStyles({
  product: {
    padding: '10px',
    margin: '5px 0'
  },
  untouched: {
    backgroundColor: '#f0f0f0' // Light gray for untouched items
  },
  inProgress: {
    backgroundColor: '#ffff99' // Light yellow for items in progress
  },
  validated: {
    backgroundColor: '#b6d7a8' // Light green for validated items
  },
  helperTextBase: {
    textAlign: 'center'
  },
  helperText: {
    textAlign: 'center',
    color: 'green'
  },
  resetButton: {
    marginTop: '1%',
    display: 'flex',
    justifyContent: 'center'
  }
})
